import type {
  ChatPage,
  ChatPageFetcher,
  ChatServerMessage,
  ChatServerWindow,
} from './types';

// A latest page that no longer overlaps known messages is connected to them
// by reading the following pages; a longer chain means the history keeps
// changing.
const MAX_BRIDGE_PAGES = 20;

/** Newest first; a later snapshot wins when a message is received twice. */
export const mergeChatMessages = <TMessage extends ChatServerMessage>(
  ...snapshots: readonly (readonly TMessage[])[]
): TMessage[] => {
  const messages = new Map<number, TMessage>();
  for (const snapshot of snapshots) {
    for (const message of snapshot) {
      messages.set(message.id, message);
    }
  }

  return [...messages.values()].sort(
    (left, right) =>
      right.createdAt.localeCompare(left.createdAt) || right.id - left.id
  );
};

const getNextPage = (page: ChatPage<ChatServerMessage> | undefined) =>
  page && page.pagination.currentPage < page.pagination.totalPages
    ? page.pagination.currentPage + 1
    : undefined;

const createWindow = <
  TMessage extends ChatServerMessage,
  TPage extends ChatPage<TMessage>,
>(
  knownMessages: readonly TMessage[],
  pages: TPage[]
): ChatServerWindow<TMessage, TPage> => {
  const nextPage = getNextPage(pages.at(-1));
  return {
    pages,
    // A newer page wins when the same message is received twice.
    messages: mergeChatMessages(
      knownMessages,
      ...[...pages].reverse().map(page => page.data)
    ),
    nextPage,
    hasMore: nextPage !== undefined,
  };
};

export const fetchLatestChatWindow = async <
  TMessage extends ChatServerMessage,
  TPage extends ChatPage<TMessage>,
>(
  fetchPage: ChatPageFetcher<TPage>,
  knownMessages: readonly TMessage[]
): Promise<ChatServerWindow<TMessage, TPage>> => {
  const firstPage = await fetchPage(1);
  const pages = [firstPage];
  const knownIds = new Set(knownMessages.map(message => message.id));
  const overlaps = (page: TPage) =>
    page.data.some(message => knownIds.has(message.id));

  if (knownIds.size && !overlaps(firstPage)) {
    // More than one page arrived between polls: read the following pages
    // until the new messages connect to the known ones.
    let current = firstPage;
    let nextPage = getNextPage(current);
    while (nextPage !== undefined && !overlaps(current)) {
      if (pages.length >= MAX_BRIDGE_PAGES) {
        throw new Error('Message history changed during refresh');
      }
      current = await fetchPage(nextPage);
      pages.push(current);
      nextPage = getNextPage(current);
    }
  }

  return createWindow(knownMessages, pages);
};

export const fetchOlderChatWindow = async <
  TMessage extends ChatServerMessage,
  TPage extends ChatPage<TMessage>,
>(
  fetchPage: ChatPageFetcher<TPage>,
  knownMessages: readonly TMessage[],
  page: number
): Promise<ChatServerWindow<TMessage, TPage>> => {
  const response = await fetchPage(page);
  const window = createWindow(knownMessages, [response]);

  // A page that does not advance must not create an observer retry loop.
  if (window.nextPage !== undefined && window.nextPage <= page) {
    throw new Error('Message history changed before older rows were loaded');
  }
  return window;
};
